import React, { useState, useEffect } from "react";
import {
  Input,
  Select,
  Pagination,
  Spin,
  Tooltip,
  Row,
  Col,
  Button,
  Card,
  Descriptions,
  Typography,
} from "antd";
import {
  SearchOutlined,
  OrderedListOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import moment from "moment";
import "../../../src/scss/ordenesExpressStyles.scss";

const { Option } = Select;
const { Text } = Typography;

export default function OrdersExpressTable({ openOrders = [], loading }) {
  const [tableData, setTableData] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [expandedRows, setExpandedRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [filterServiceCount, setFilterServiceCount] = useState(null);

  const columnWidths = [
    { width: "12%", name: "orden" }, // Orden
    { width: "8%", name: "servicios" }, // Servicios
    { width: "40%", name: "titulo" }, // Título
    { width: "15%", name: "cliente" }, // Cliente
    { width: "13%", name: "actualizacion" }, // Última Actualización
    { width: "12%", name: "antiguedad" }, // Fecha de Creación
  ];

  useEffect(() => {
    const data = openOrders
      .map((order, idx) => {
        const rawAttachments =
          order.attachments ??
          order.attachment ??
          order.files ??
          order.fileList ??
          [];

        let normalizedAttachments = [];
        if (Array.isArray(rawAttachments)) {
          normalizedAttachments = rawAttachments;
        } else if (typeof rawAttachments === "string") {
          const trimmed = rawAttachments.trim();
          if (trimmed.startsWith("[") || trimmed.startsWith("{")) {
            try {
              const parsed = JSON.parse(trimmed);
              if (Array.isArray(parsed)) {
                normalizedAttachments = parsed;
              } else if (parsed && typeof parsed === "object") {
                normalizedAttachments = Object.values(parsed);
              }
            } catch (error) {
              normalizedAttachments = [rawAttachments];
            }
          } else if (trimmed.length > 0) {
            normalizedAttachments = [rawAttachments];
          }
        } else if (rawAttachments && typeof rawAttachments === "object") {
          normalizedAttachments = Object.values(rawAttachments);
        }

        const inferredServiceCount = Array.isArray(order?.services)
          ? order.services.length
          : Array.isArray(order?.items)
            ? order.items.length
            : 1;

        return {
          key: idx,
          ...order,
          attachments: normalizedAttachments,
          updated_ts: moment(order.updated_at).valueOf(),
          created_ts: moment(order.created_at).valueOf(),
          service_count: Math.max(1, inferredServiceCount),
          order_id: order.id,
          requester_name: order.name || order.requester_name || "",
          order_title: order.product_name || order.product || order.name || "",
          client_bussiness:
            order.bussiness || order.business || order.company || "",
          days_of_age: moment(order.created_at).fromNow(),
          originalOrder: {
            ...order,
            attachments: normalizedAttachments,
          },
        };
      })
      .sort((a, b) => b.updated_ts - a.updated_ts);

    setTableData(data);
  }, [openOrders]);

  const filteredData = tableData.filter((order) => {
    const searchLower = searchText.toLowerCase().trim();
    const matchesSearch =
      !searchLower ||
      String(order.id || "").toLowerCase().includes(searchLower) ||
      String(order.requester_name || "").toLowerCase().includes(searchLower) ||
      String(order.name || "").toLowerCase().includes(searchLower) ||
      String(order.bussiness || "").toLowerCase().includes(searchLower) ||
      String(order.business || "").toLowerCase().includes(searchLower) ||
      String(order.company || "").toLowerCase().includes(searchLower) ||
      String(order.product_name || "").toLowerCase().includes(searchLower);

    let matchesServiceCount = true;
    if (filterServiceCount !== null) {
      if (filterServiceCount === "3+") {
        matchesServiceCount = (order.service_count || 0) >= 3;
      } else {
        matchesServiceCount = (order.service_count || 0) === Number(filterServiceCount);
      }
    }

    return matchesSearch && matchesServiceCount;
  });

  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(filteredData.length / pageSize));
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [filteredData.length, pageSize, currentPage]);

  const getServiceBadgeColor = (count) => {
    switch (count) {
      case 1:
        return "#77AFF8";
      case 2:
        return "#296CF2";
      case 3:
      default:
        return "#343BBF";
    }
  };

  const getServiceBadgeClass = (count) => {
    return `service-badge service-count-${count}`;
  };

  const handleRowClick = (orderId) => {
    setExpandedRows((prev) =>
      prev.includes(orderId)
        ? prev.filter((id) => id !== orderId)
        : [...prev, orderId]
    );
  };

  const handlePageChange = (page, newPageSize) => {
    setCurrentPage(page);
    if (newPageSize) {
      setPageSize(newPageSize);
    }
  };

  const handleServiceCountChange = (val) => {
    setFilterServiceCount(val);
    setCurrentPage(1);
  };

  const handleSearchChange = (e) => {
    setSearchText(e.target.value);
    setCurrentPage(1);
  };

  const paginatedOrders = filteredData.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const resetFilters = () => {
    setSearchText("");
    setFilterServiceCount(null);
    setCurrentPage(1);
  };

  const resolveAttachmentHref = (attachment) => {
    if (!attachment) return null;

    if (typeof attachment === "object" && attachment?.base64) {
      const mimeType = attachment?.type || "application/octet-stream";
      return `data:${mimeType};base64,${attachment.base64}`;
    }

    const rawValue =
      typeof attachment === "string"
        ? attachment
        : attachment?.url ||
          attachment?.href ||
          attachment?.path ||
          attachment?.file ||
          attachment?.src ||
          "";

    if (!rawValue) return null;

    if (
      rawValue.startsWith("http://") ||
      rawValue.startsWith("https://") ||
      rawValue.startsWith("data:")
    ) {
      return rawValue;
    }

    if (rawValue.startsWith("/")) {
      return rawValue;
    }

    const normalized = rawValue.replace(/^data:[^;]+;base64,/, "").trim();
    const isBase64 =
      /^[A-Za-z0-9+/]+={0,2}$/.test(normalized) && normalized.length > 40;
    if (isBase64) {
      return `data:application/octet-stream;base64,${normalized}`;
    }

    return rawValue;
  };

  const getAttachmentLabel = (attachment, index) => {
    if (typeof attachment === "object" && attachment) {
      return (
        attachment.name ||
        attachment.filename ||
        attachment.fileName ||
        `Archivo ${index + 1}`
      );
    }
    return `Archivo ${index + 1}`;
  };

  const getAttachmentType = (attachment, href) => {
    const source = `${
      typeof attachment === "object" && attachment
        ? attachment.type ||
          attachment.mime ||
          attachment.name ||
          attachment.filename ||
          ""
        : attachment || ""
    } ${href || ""}`.toLowerCase();

    if (
      source.includes("image/") ||
      source.match(/\.(png|jpe?g|gif|webp|bmp|svg)(\?|$)/)
    ) {
      return "Imagen";
    }
    if (source.includes("pdf") || source.match(/\.pdf(\?|$)/)) {
      return "PDF";
    }
    if (source.includes("word") || source.match(/\.(doc|docx)(\?|$)/)) {
      return "Documento";
    }
    if (
      source.includes("excel") ||
      source.includes("spreadsheet") ||
      source.match(/\.(xls|xlsx|csv)(\?|$)/)
    ) {
      return "Excel";
    }
    if (source.includes("text/plain") || source.match(/\.txt(\?|$)/)) {
      return "Texto";
    }
    return "Archivo";
  };

  return (
    <div className="express-table-container">
      <Row gutter={[16, 16]} className="filters-row">
        <Col xs={24} sm={12} md={8} lg={6}>
          <Select
            value={filterServiceCount}
            onChange={handleServiceCountChange}
            style={{ width: "100%" }}
            placeholder={
              <span>
                <OrderedListOutlined style={{ marginRight: 8 }} />
                Servicios
              </span>
            }
          >
            <Option value={null}>Todos los servicios</Option>
            <Option value="1">1 servicio</Option>
            <Option value="2">2 servicios</Option>
            <Option value="3+">3 o más servicios</Option>
          </Select>
        </Col>

        <Col xs={24} sm={12} md={8} lg={12}>
          <Input
            placeholder="Buscar por ID, nombre, empresa o producto..."
            value={searchText}
            onChange={handleSearchChange}
            prefix={<SearchOutlined />}
            allowClear
            onClear={() => {
              setSearchText("");
              setCurrentPage(1);
            }}
          />
        </Col>

        {(searchText || filterServiceCount !== null) && (
          <Col xs={24} sm={12} md={8} lg={6}>
            <Button onClick={resetFilters} block>
              Restablecer Filtros
            </Button>
          </Col>
        )}
      </Row>

      <div className="table-container">
        {loading ? (
          <div className="loading-container">
            <Spin size="large" />
            <p>Cargando órdenes...</p>
          </div>
        ) : (
          <table className="styled-table">
            <colgroup>
              <col style={{ width: "12%" }} />
              <col style={{ width: "8%" }} />
              <col style={{ width: "40%" }} />
              <col style={{ width: "15%" }} />
              <col style={{ width: "13%" }} />
              <col style={{ width: "12%" }} />
            </colgroup>
            <thead>
              <tr>
                <th className="header-cell orden">Orden</th>
                <th className="header-cell servicios">Servicios</th>
                <th className="header-cell titulo">Título</th>
                <th className="header-cell cliente">Cliente</th>
                <th className="header-cell actualizacion">
                  Última Actualización
                </th>
                <th className="header-cell antiguedad">Fecha de Creación</th>
              </tr>
            </thead>
            <tbody>
              {paginatedOrders.length === 0 ? (
                <tr>
                  <td colSpan="6" className="empty-state-cell">
                    {searchText || filterServiceCount !== null ? (
                      <div className="no-results">
                        <p>
                          No se encontraron órdenes con los filtros aplicados
                        </p>
                        <button
                          className="clear-filters-btn empty-state-btn"
                          onClick={resetFilters}
                        >
                          Limpiar filtros
                        </button>
                      </div>
                    ) : (
                      <p>No hay órdenes disponibles</p>
                    )}
                  </td>
                </tr>
              ) : (
                paginatedOrders.map((order) => (
                  <React.Fragment key={order.order_id}>
                    <tr
                      className="table-row"
                      onClick={() => handleRowClick(order.order_id)}
                    >
                      <td className="body-cell orden">
                        <span className="order-plain-text">
                          {order.order_id}
                        </span>
                      </td>
                      <td className="body-cell servicios">
                        <div
                          className={getServiceBadgeClass(order.service_count)}
                          style={{
                            backgroundColor: getServiceBadgeColor(
                              order.service_count
                            ),
                          }}
                        >
                          {order.service_count}
                        </div>
                      </td>
                      <td className="body-cell titulo">
                        {order.order_title.toUpperCase()}
                      </td>
                      <td className="body-cell cliente">
                        {order.client_bussiness.toUpperCase()}
                      </td>
                      <td className="body-cell actualizacion">
                        <Tooltip
                          title={moment(order.updated_at).format(
                            "DD/MM/YYYY HH:mm"
                          )}
                        >
                          {moment(order.updated_at).fromNow()}
                        </Tooltip>
                      </td>
                      <td className="body-cell antiguedad">
                        {moment(order.created_at).format("DD/MM/YYYY")}
                      </td>
                    </tr>
                    {expandedRows.includes(order.order_id) && (
                      <tr className="expanded-row">
                        <td colSpan="6" className="expanded-cell">
                          <div className="expanded-content">
                            <div className="services-section">
                              <div className="section-title">
                                Detalles de la Orden {order.order_id}
                              </div>

                              <Card size="small" className="details-card">
                                <Descriptions
                                  column={1}
                                  size="small"
                                  className="details-descriptions"
                                >
                                  <Descriptions.Item label="ID">
                                    <Text>{order.originalOrder.id}</Text>
                                  </Descriptions.Item>

                                  <Descriptions.Item label="Cantidad">
                                    <Text>{order.originalOrder.quantity}</Text>
                                  </Descriptions.Item>

                                  {(order.originalOrder.name ||
                                    order.originalOrder.requester_name) && (
                                    <Descriptions.Item label="Nombre del requisitor">
                                      <Text>
                                        {order.originalOrder.name ||
                                          order.originalOrder.requester_name}
                                      </Text>
                                    </Descriptions.Item>
                                  )}

                                  {(order.originalOrder.product_name ||
                                    order.originalOrder.product) && (
                                    <Descriptions.Item label="Nombre del producto">
                                      <Text>
                                        {order.originalOrder.product_name ||
                                          order.originalOrder.product}
                                      </Text>
                                    </Descriptions.Item>
                                  )}

                                  {(order.originalOrder.bussiness ||
                                    order.originalOrder.business ||
                                    order.originalOrder.company) && (
                                    <Descriptions.Item label="Empresa">
                                      <Text>
                                        {order.originalOrder.bussiness ||
                                          order.originalOrder.business ||
                                          order.originalOrder.company}
                                      </Text>
                                    </Descriptions.Item>
                                  )}

                                  {order.originalOrder.description && (
                                    <Descriptions.Item label="Descripción">
                                      <Text>
                                        {order.originalOrder.description}
                                      </Text>
                                    </Descriptions.Item>
                                  )}

                                  {order.originalOrder.department && (
                                    <Descriptions.Item label="Departamento">
                                      <Text>
                                        {order.originalOrder.department}
                                      </Text>
                                    </Descriptions.Item>
                                  )}

                                  {order.originalOrder.email && (
                                    <Descriptions.Item label="Email">
                                      <Text>{order.originalOrder.email}</Text>
                                    </Descriptions.Item>
                                  )}

                                  {order.originalOrder.attachments?.length >
                                    0 && (
                                    <Descriptions.Item label="Archivos">
                                      {order.originalOrder.attachments.map(
                                        (attachment, i) => {
                                          const href =
                                            resolveAttachmentHref(attachment);
                                          const fileType = getAttachmentType(
                                            attachment,
                                            href
                                          );
                                          const fileLabel = getAttachmentLabel(
                                            attachment,
                                            i
                                          );
                                          if (!href) {
                                            return (
                                              <div
                                                key={i}
                                                className="attachment-link"
                                              >
                                                Archivo {i + 1} no disponible
                                              </div>
                                            );
                                          }

                                          return (
                                            <div
                                              key={i}
                                              className="attachment-link attachment-row"
                                            >
                                              <a
                                                href={href}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="attachment-open-link"
                                              >
                                                {fileLabel}
                                              </a>
                                              <span className="attachment-type">
                                                {fileType}
                                              </span>
                                            </div>
                                          );
                                        }
                                      )}
                                    </Descriptions.Item>
                                  )}

                                  <Descriptions.Item label="Status">
                                    <Text>{order.originalOrder.status}</Text>
                                  </Descriptions.Item>
                                </Descriptions>

                                <div className="details-footer">
                                  <Text
                                    type="secondary"
                                    className="details-updated"
                                  >
                                    <ClockCircleOutlined
                                      style={{ marginRight: 4 }}
                                    />
                                    Actualizado:{" "}
                                    {moment(order.updated_at).fromNow()}
                                  </Text>
                                </div>
                              </Card>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      {filteredData.length > 0 && (
        <div className="pagination-container">
          <Pagination
            current={currentPage}
            pageSize={pageSize}
            total={filteredData.length}
            showSizeChanger={true}
            pageSizeOptions={["10", "20", "50", "100"]}
            showQuickJumper={false}
            showTotal={(total, range) =>
              `${range[0]}-${range[1]} de ${total} órdenes`
            }
            onChange={handlePageChange}
            onShowSizeChange={handlePageChange}
          />
        </div>
      )}
    </div>
  );
}
